import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, MapPin, Phone, Globe, Mail, Clock, Star, Facebook, Instagram, Twitter } from 'lucide-react';
import { businessApi } from '../services/api';
import ShareButtons from '../components/ShareButtons';
import Linkify from '../components/Linkify';
import ReviewsSection from '../components/ReviewsSection';

const BusinessDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  useEffect(() => {
    loadBusiness();
  }, [slug]);

  const loadBusiness = async () => {
    try {
      const response = await businessApi.getById(slug);
      setBusiness(response.data);
      // Redirect UUID URLs to slug URLs for SEO
      if (response.data.slug && response.data.slug !== slug) {
        navigate(`/businesses/${response.data.slug}`, { replace: true });
      }
    } catch (error) {
      console.error('Error loading business:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Business not found</h2>
          <Link to="/businesses" className="btn-primary">Back to Directory</Link>
        </div>
      </div>
    );
  }

  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const pageUrl = `${siteUrl}/businesses/${business.slug || slug}`;
  const ogImage = business.photos?.length > 0 
    ? `${siteUrl}${business.photos[0]}` 
    : `${siteUrl}/og-image.png`;

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.business_name,
    "description": business.description || "",
    "url": pageUrl,
    "image": ogImage,
    "address": {
      "@type": "PostalAddress",
      ...(business.street_address && { streetAddress: business.street_address }),
      "addressLocality": business.city_state_zip || "Martinsville, VA",
      "addressRegion": "VA"
    },
    ...(business.phone && { telephone: business.phone }),
    ...(business.email && { email: business.email }),
    ...(business.website && { sameAs: [business.website] }),
    ...(business.primary_category && { additionalType: business.primary_category }),
    ...(business.hours_of_operation && { openingHours: business.hours_of_operation }),
    "isPartOf": { "@type": "WebSite", "name": "Explore MHC", "url": siteUrl }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="business-detail">
      <Helmet>
        <title>{`${business.business_name} | Explore MHC`}</title>
        <meta name="description" content={business.description ? business.description.substring(0, 160) : ''} />
        <meta property="og:type" content="business.business" />
        <meta property="og:title" content={`${business.business_name} | Explore MHC`} />
        <meta property="og:description" content={business.description ? business.description.substring(0, 200) : ''} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Explore MHC" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${business.business_name} | Explore MHC`} />
        <meta name="twitter:description" content={business.description ? business.description.substring(0, 200) : ''} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/businesses" className="inline-flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery */}
            {business.photos?.length > 0 ? (
              <div className="space-y-4">
                <div className="aspect-video bg-white rounded-2xl overflow-hidden border border-neutral-200">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[selectedPhoto]}`}
                    alt={business.business_name}
                    className="w-full h-full object-contain"
                  />
                </div>
                {business.photos.length > 1 && (
                  <div className="flex gap-2">
                    {business.photos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedPhoto(index)}
                        className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedPhoto === index ? 'border-primary' : 'border-transparent'
                        }`}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video bg-white rounded-2xl flex items-center justify-center border border-neutral-200">
                <span className="text-9xl font-bold text-neutral-300">
                  {business.business_name.charAt(0)}
                </span>
              </div>
            )}

            {/* Business Info */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 border border-neutral-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="min-w-0 flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-2 break-words" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    {business.business_name}
                  </h1>
                  <p className="text-neutral-600">
                    {business.primary_category}
                    {business.subcategory && <span> &middot; {business.subcategory}</span>}
                  </p>
                  {business.secondary_category && (
                    <p className="text-neutral-500 text-sm">Also in: {business.secondary_category}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap shrink-0">
                  <ShareButtons 
                    type="business" 
                    slug={business.slug || slug}
                    title={business.business_name}
                    description={business.description}
                  />
                  {business.is_featured && (
                    <span className="badge badge-featured flex items-center gap-1 whitespace-nowrap">
                      <Star className="w-4 h-4" /> Featured
                    </span>
                  )}
                </div>
              </div>

              {/* Ownership Tags */}
              {business.ownership_tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {business.ownership_tags.map(tag => (
                    <span key={tag} className="badge badge-minority">{tag}</span>
                  ))}
                </div>
              )}

              <p className="text-neutral-700 leading-relaxed mb-6 whitespace-pre-wrap break-words">
                <Linkify text={business.description} />
              </p>

              {/* Hours */}
              {business.hours && Object.keys(business.hours).length > 0 && (
                <div className="border-t border-neutral-200 pt-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" /> Hours of Operation
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {DAYS.map(day => (
                      <div key={day} className="flex justify-between py-2 border-b border-neutral-100">
                        <span className="font-medium">{day}</span>
                        <span className="text-neutral-600">
                          {business.hours[day.toLowerCase()] || 'Closed'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Reviews */}
            <ReviewsSection businessId={business.id} businessName={business.business_name} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200">
              <h3 className="font-bold text-lg mb-4">Contact Information</h3>
              
              <div className="space-y-4">
                {(business.street_address || business.city_state_zip) && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    {business.street_address && <p className="font-medium">{business.street_address}</p>}
                    <p className="text-neutral-600">{business.city_state_zip}</p>
                  </div>
                </div>
                )}

                {business.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary" />
                    <a href={`tel:${business.phone}`} className="text-primary hover:underline">
                      {business.phone}
                    </a>
                  </div>
                )}

                {business.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <a 
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Visit Website
                    </a>
                  </div>
                )}

                {business.contact_email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <a href={`mailto:${business.contact_email}`} className="text-primary hover:underline">
                      Send Email
                    </a>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {(business.facebook || business.instagram || business.twitter) && (
                <div className="border-t border-neutral-200 mt-6 pt-6">
                  <h4 className="font-medium mb-3">Follow Us</h4>
                  <div className="flex gap-3">
                    {business.facebook && (
                      <a
                        href={business.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {business.instagram && (
                      <a
                        href={business.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {business.twitter && (
                      <a
                        href={business.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Google Maps */}
            {(business.street_address || business.city_state_zip) && (
            <div className="bg-white rounded-2xl p-6 border border-neutral-200">
              <h3 className="font-bold text-lg mb-4">Location</h3>
              <div className="aspect-square rounded-xl overflow-hidden">
                <iframe
                  title="Business Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent((business.street_address ? business.street_address + ', ' : '') + (business.city_state_zip || ''))}&output=embed`}
                  allowFullScreen
                />
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetail;
